import React, { Fragment, ReactNode } from 'react';
import styles from './ScreeningOverviewStepTable.module.css';
import Text from '../../atoms/typography/Text';
import { ColorEnum, FontSizeEnum, FontWeightEnum } from '../../enums';
import { classNames } from '../../utils';
import { useTranslation } from 'react-i18next';
import { StoredFileMetadata } from '../../../api/shared/types';
import { DocumentLink } from '../document-link';

export type DataValue = undefined | null | string | number | StoredFileMetadata | StoredFileMetadata[];

export interface DataRow {
    title: ReactNode;
    value: DataValue;
    optional?: boolean;
}

export interface StepTableSubSection {
    title: ReactNode;
    value?: DataValue;
    data?: DataRow[];
    optional?: boolean;
}

export interface StepTableSection {
    title?: ReactNode;
    subSections: StepTableSubSection[];
}

interface ScreeningOverviewStepTableProps {
    data: StepTableSection[];
}

const ScreeningOverviewStepTable = ({
    data = []
}: ScreeningOverviewStepTableProps) => {
    const { t } = useTranslation();
    const renderFile = (file: StoredFileMetadata, index: number): ReactNode => {
        return (
            <div key={index} className={styles.file}>
                <DocumentLink
                    fileName={file.name}
                    params={file.params}
                />
            </div>
        );
    }

    const displayValue = (value: DataValue): ReactNode => {
        if (value === undefined) {
            return '';
        }

        if (value === null) {
            return (
                <Text color={ColorEnum.Gray500}>-</Text>
            );
        }

        if (typeof value === 'string' || typeof value === 'number') {
            return (
                <Text color={ColorEnum.Gray500}>{value}</Text>
            );
        }

        if ('size' in value && 'name' in value) {
            return renderFile(value as StoredFileMetadata, 0);
        }

        if (Array.isArray(value) && value.length > 0 && 'size' in value[0] && 'name' in value[0]) {
            return (
                <span>
                    {value.map((file, index) => renderFile(file as StoredFileMetadata, index))}
                </span>
            );
        }

        return '-';
    }

    const renderRow = (row: StepTableSubSection | DataRow, headerRow = false): ReactNode => {
        const titleWeight = headerRow ? FontWeightEnum.Semibold : FontWeightEnum.Regular;

        return (
            <>
                <div className={styles.mobileRow}>
                    <Text weight={titleWeight}>
                        {row.title}
                        {row.optional && (
                            <>
                                <span>&nbsp;</span>
                                <Text
                                    color={ColorEnum.Gray400}
                                    size={FontSizeEnum.Md}
                                    weight={FontWeightEnum.Regular}
                                >({t('optional')})</Text>
                            </>
                        )}
                    </Text>
                </div>
                <div>
                    <Text
                        color={ColorEnum.Gray500}
                        weight={FontWeightEnum.Regular}
                    >{displayValue(row.value)}</Text>
                </div>
            </>
        );
    }

    return (
        <div className={styles.table}>
            {data.map((step, index) => (
                <div key={`section-${index}`} className={styles.section}>
                    {step.title && (
                        <div className={styles.title}>{step.title}</div>
                    )}
                    <div className={styles.divider}></div>
                    {step.subSections.map((subSection, subIndex) => (
                        <Fragment key={subIndex}>
                            <div key={`subsectionRow-${(index + 1) * (subIndex + 1)}`} className={styles.row}>
                                {renderRow(subSection, true)}
                            </div>
                            {subSection.data?.map((dataRow, dataIndex) => (
                                <div key={`dataRow-${(index + 1) * (subIndex + 1) * (dataIndex + 1)}`} className={classNames(styles.row, styles.nested)}>
                                    {renderRow(dataRow)}
                                </div>
                            ))}
                        </Fragment>
                    ))}
                </div>
            ))}
        </div>
    );
}

export default ScreeningOverviewStepTable;
